/* Image-only collection. Bounded DOM, unbounded coordinates; no animation dependency. */
(() => {
  if(document.querySelector('vorug-infinite-collection [data-page-recovery-url]')){
    const url=new URL(location.href);if(url.searchParams.has('page')){url.searchParams.delete('page');location.replace(url.href);}
  }
  class VorugInfiniteCollection extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.baseRenderReady = document.readyState === 'complete' || (performance.getEntriesByType('navigation')[0]?.domContentLoadedEventStart || 0) > 0;
      if (!this.baseRenderReady) document.addEventListener('DOMContentLoaded', () => { this.baseRenderReady = true; this.schedule(); }, { once: true, signal: this.signal });
      this.viewport = this.querySelector('[data-viewport]');
      this.field = this.querySelector('[data-field]');
      this.source = this.querySelector('[data-source]');
      this.status = this.querySelector('[data-status]');
      this.notice = this.querySelector('[data-notice]');
      this.products = [...this.source.querySelectorAll('a')];
      this.selection = { shape: '', style: '', color: '', origin: '', construction: '' };
      this.x = 0; this.y = 0; this.cells = new Map();
      this.initDiscovery();
      this.readProducts();
      this.buildFilters();
      this.listen();
      this.resize = new ResizeObserver(() => this.schedule());
      this.resize.observe(this.viewport);
      this.schedule();
      this.loadRemaining();
    }
    disconnectedCallback() {
      this.dispatchEvent(new CustomEvent('vorug:disconnected'));
      this.controller?.abort(); this.resize?.disconnect(); this.impressions?.disconnect(); this.finishTransition();
      this.stopMomentum();
      for (const timer of this.exposureTimers?.values() || []) clearTimeout(timer);
      cancelAnimationFrame(this.frame);
    }
    readProducts() {
      for (const a of this.products) {
        const signature=[a.dataset.tags,a.dataset.shape,a.dataset.type,a.href].join('\u001f');
        if(a._vicFacetSignature===signature)continue;
        a._vicFacetSignature=signature;
        const tags = (a.dataset.tags || '').toLowerCase().split('|').map(t => t.trim()).filter(Boolean);
        a.dataset.productId ||= new URL(a.href, location.href).pathname.split('/').filter(Boolean).pop();
        a.facets = { shape: this.shapeFor(a, tags),
          construction: this.tagFacets(tags, 'construction', ['pile','flatweave']).concat(tags.includes('hand-knotted')?['pile']:[]),
          origin: this.tagFacets(tags, 'origin', ['beni-mrirt','beni-ourain','boujad','azilal','taznakht']),
          style: this.tagFacets(tags, 'style', ['patterns','solids','neutrals','colorful']),
          color: this.tagFacets(tags, 'color', ['beige','cream','white','black','brown','blue','green','red','pink','purple','yellow','orange','multicolor','grey','gray']) };
      }
      if(this.dataset.canonicalTaxonomy==='true')for(const a of this.products){
        const facts=window.VorugDiscoveryTaxonomy?.[new URL(a.href,location.href).pathname.split('/').pop()];
        a.facets=facts?{shape:facts.shape,style:[...facts.style],color:[...facts.color],origin:[...(facts.origin||[])],construction:[...(facts.construction||[])]}:{shape:null,style:[],color:[],origin:[],construction:[]};
        a.dataset.familyId=facts?.family||a.dataset.productId;
      }
      this.filtered = this.products.filter(a => this.isDiscoverable(a) && (!this.selection.shape || a.facets.shape === this.selection.shape) && (!this.selection.origin || a.facets.origin.includes(this.selection.origin)) && (!this.selection.construction || a.facets.construction.includes(this.selection.construction)) && (!this.selection.style || a.facets.style.includes(this.selection.style)) && (!this.selection.color || this.colorMatches(a.facets.color,this.selection.color)));
      this.seedHistory();
      this.rankProducts();
      if(!this.selection.shape)this.groupFamilies();
      this.context = JSON.stringify(this.selection);
      this.status.textContent = `${this.filtered.length} rugs. Use arrow keys to explore, Tab to visit a rug or change filters.`;
      this.notice.hidden = this.filtered.length > 0;
    }
    colorMatches(colors,value) {
      const groups={light:['cream','white','beige'],dark:['black','grey','black-white'],warm:['red','orange','yellow','terracotta-rust'],rose:['pink','purple','burgundy']};
      return (groups[value]||[value==='gray'?'grey':value]).some(c=>colors.includes(c));
    }
    familyKey(product) {
      if(product.dataset.familyId)return product.dataset.familyId;
      if(product._vicFamilyHref!==product.href){product._vicFamilyHref=product.href;product._vicFamilyKey=new URL(product.href,location.href).pathname.split('/').pop().replace(/-(area-rug|round-rug|runner|round)$/,'');}
      return product._vicFamilyKey;
    }
    familyGroups() {
      if(this._vicGroupSource===this.filtered)return this._vicGroups;
      const groups=new Map();
      this.filtered.forEach((product,index)=>{
        const key=this.familyKey(product);
        if(!groups.has(key))groups.set(key,[]);groups.get(key).push(index);
      });
      this._vicGroupSource=this.filtered;return this._vicGroups=[...groups.values()];
    }
    groupFamilies() {
      const families=new Map(),order={Area:0,Runner:1,Round:2};
      for(const product of this.filtered){const key=this.familyKey(product);if(!families.has(key))families.set(key,[]);families.get(key).push(product);}
      this.filtered=[...families.values()].sort((a,b)=>Number(b.length>=3)-Number(a.length>=3)).flatMap(items=>items.sort((a,b)=>(order[a.facets.shape]??3)-(order[b.facets.shape]??3)));
    }
    tagFacets(tags, kind, legacy) {
      const prefixed = tags.filter(t => t.startsWith(`${kind}:`)).map(t => t.slice(kind.length + 1).trim()).filter(Boolean);
      return [...new Set((prefixed.length ? prefixed : tags.filter(t => legacy.includes(t))).map(t => kind === 'color' && t === 'gray' ? 'grey' : t))];
    }
    isDiscoverable(a) {
      return a.dataset.discoverable !== 'false' && (a.dataset.available !== 'false' || this.dataset.includeUnavailable === 'true');
    }
    stopMomentum() {
      cancelAnimationFrame(this.momentumFrame); this.momentumFrame=0;
    }
    coast(vx,vy) {
      this.stopMomentum();
      if(matchMedia('(prefers-reduced-motion: reduce)').matches || Math.hypot(vx,vy)<.12)return;
      const started=performance.now();let previous=started;
      const tick=now=>{
        const dt=Math.min(32,now-previous);previous=now;
        const decay=Math.exp(-dt/145);vx*=decay;vy*=decay;
        this.x+=vx*dt;this.y+=vy*dt;this.schedule();
        if(now-started<800&&Math.hypot(vx,vy)>.025)this.momentumFrame=requestAnimationFrame(tick);
        else this.momentumFrame=0;
      };
      this.momentumFrame=requestAnimationFrame(tick);
    }
    prepareImage(image,width=900) {
      if(!image)return;
      const source=image.dataset.originalSrc||image.dataset.src||image.getAttribute('src');if(!source)return;
      if(Number(image.dataset.requestWidth)>=width)return;
      image.dataset.originalSrc=source;image.dataset.requestWidth=String(width);
      if(image.dataset.imageState==='loaded'&&image.src)image.dataset.fallbackSrc=image.src;
      try { const url=new URL(source,location.href);if(url.hostname==='cdn.shopify.com'||(url.origin===new URL(location.href).origin&&url.pathname.startsWith('/cdn/shop/files/'))){url.searchParams.set('width',String(width));if(this.dataset?.journey==='on')url.searchParams.set('quality',String(width<=480?82:width<=960?85:90))}image.src=url.href; }
      catch { image.src=source; }
      image.removeAttribute('data-src');image.decoding='async';
    }
    shapeFor(a, tags) {
      const tagged = tags.find(t => ['shape:runner','shape:round','shape:area'].includes(t));
      if(tagged) { const value=tagged.slice(6); return value[0].toUpperCase()+value.slice(1); }
      const explicit=(a.dataset.shape || '').toLowerCase();
      if(['runner','round','area'].includes(explicit)) return explicit[0].toUpperCase()+explicit.slice(1);
      for(const shape of ['runner','round','area']) {
        if(tags.includes(shape) || tags.includes(`shape:${shape}`) || new RegExp(`\\b${shape}\\b`, 'i').test(a.dataset.type || '')) return shape[0].toUpperCase()+shape.slice(1);
      }
      return ''; // Unknown metadata stays unknown, rather than silently becoming Area.
    }
    initDiscovery() {
      this.ranking=new URLSearchParams(location.search).get('ranking')==='curated' ? 'curated' : 'relevance';
      this.affinity=new Map(); this.visits=[];
      try{const profile=JSON.parse(localStorage.getItem('vorug.discovery.visits.v1')||'null');if(profile?.schema===1&&Array.isArray(profile.visits))this.visits=profile.visits.filter(v=>typeof v?.id==='string'&&Number.isFinite(v.at)&&v.at<=Date.now()+60000&&Date.now()-v.at<30*86400000&&Number.isFinite(v.weight)&&v.weight>0).slice(-40)}catch{}
      this.events=[]; this.seen=new Set(); this.conversions=new Set();
      this.exposureTimers=new Map(); this.exposureEntries=new Map();
      this.context=''; this.eventSequence=0;
      this.impressions=new IntersectionObserver(entries=>{
        for(const entry of entries) {
          this.exposureEntries.set(entry.target,entry.isIntersecting && entry.intersectionRatio>=.5);
          this.armExposure(entry.target);
        }
      }, {root:this.viewport,rootMargin:'0px 0px -160px 0px',threshold:[0,.5]});
      document.addEventListener('visibilitychange',()=>{
        for(const img of this.exposureEntries.keys()) this.armExposure(img);
      },{signal:this.signal});
      // Integration boundary: a trusted storefront/cart adapter supplies confirmed actions.
      document.addEventListener('vorug:conversion',e=>{
        const d=e.detail || {};
        if(!['add_to_cart','purchase'].includes(d.type) || !d.eventId || !d.productId) return;
        const product=this.products.find(p=>p.dataset.productId===String(d.productId));
        const key=`${d.type}:${d.eventId}:${d.productId}`;
        if(!product || this.conversions.has(key)) return;
        this.conversions.add(key);
        this.learn(product,d.type==='purchase' ? 5 : 3);
        this.emitDiscovery(d.type,{productId:String(d.productId),conversionId:String(d.eventId),value:Number.isFinite(d.value)&&d.value>=0?d.value:null,currency:/^[A-Z]{3}$/.test(d.currency||'')?d.currency:null});
      },{signal:this.signal});
    }
    seedHistory() {
      if(this._vicHistorySeeded)return;
      this._vicHistorySeeded=true;this.visits ||= [];this.affinity ||= new Map();
      const params=new URL(location.href).searchParams;let handle=params.get('entry');
      if(!handle)try{const ref=new URL(document.referrer);const host=new URL(this.products[0]?.href,location.href).hostname;if(ref.hostname===host&&ref.pathname.startsWith('/products/'))handle=ref.pathname.split('/')[2]}catch{}
      const entry=this.products.find(p=>p.dataset.productId===handle||new URL(p.href,location.href).pathname.split('/').pop()===handle);
      if(entry)this.learn(entry,3);else if(this.visits.length)this.rebuildAffinity();
    }
    rebuildAffinity() {
      this.affinity.clear();const products=new Map();for(const p of this.products){products.set(p.dataset.productId,p);products.set('handle:'+new URL(p.href,location.href).pathname.split('/').pop(),p)}
      for(const visit of this.visits){const p=products.get(visit.id);if(!p?.facets)continue;const age=Math.max(0,Date.now()-visit.at)/86400000,weight=Math.min(5,visit.weight)*Math.exp(-age/7);
        for(const token of [p.facets.shape,...p.facets.style,...p.facets.color].filter(Boolean))this.affinity.set(token,Math.min(10,(this.affinity.get(token)||0)+weight));}
    }
    learn(product, weight) {
      if(!product?.facets)return;
      this.visits=(this.visits||[]).filter(v=>v.id!==product.dataset.productId&&Date.now()-v.at<30*86400000);
      this.visits.push({id:product.dataset.productId,at:Date.now(),weight:Math.max(1,Math.min(5,weight))});this.visits=this.visits.slice(-40);
      this.rebuildAffinity();
      try{localStorage.setItem('vorug.discovery.visits.v1',JSON.stringify({schema:1,visits:this.visits}))}catch{}
      // Local product visits only. Apply ranking on the next explicit change, never during a gesture.
    }
    rankProducts() {
      if(this.ranking==='curated')return;
      const affinity=this.affinity||new Map(),familyScores=new Map();
      const scored=this.filtered.map((product,index)=>{const score=[product.facets.shape,...product.facets.style,...product.facets.color].reduce((sum,t)=>sum+(affinity.get(t)||0),0)+Math.max(0,Math.min(10,Number(product.dataset.priority)||0));const key=this.familyKey(product);familyScores.set(key,Math.max(score,familyScores.get(key)||0));return {product,index,score,key};});
      // One stable sort: family relevance in All; product relevance in a single shape.
      scored.sort((a,b)=>(this.selection.shape?b.score-a.score:familyScores.get(b.key)-familyScores.get(a.key))||a.index-b.index);
      this.filtered=scored.map(item=>item.product);
    }
    emitDiscovery(type,detail={}) {
      const event={schema:1,type,sequence:++this.eventSequence,timestamp:Date.now(),ranking:this.ranking,selection:{...this.selection},...detail};
      this.events.push(event);if(this.events.length>200)this.events.shift();
      this.dispatchEvent(new CustomEvent('vorug:discovery',{detail:event,bubbles:true}));
      // Local diagnostics only: no personal identifiers, cookies, storage or network transmission.
    }
    armExposure(img) {
      clearTimeout(this.exposureTimers.get(img));this.exposureTimers.delete(img);
      const tile=img.closest('a'); if(!tile) return;
      const context=this.context,key=`${context}:${tile.dataset.productId}`;
      if(document.hidden || !this.exposureEntries.get(img) || this.seen.has(key) || !img.complete || !img.naturalWidth) return;
      this.exposureTimers.set(img,setTimeout(()=>{
        this.exposureTimers.delete(img);
        if(!img.isConnected || document.hidden || this.context!==context || !this.exposureEntries.get(img) || this.seen.has(key)) return;
        this.seen.add(key);
        this.emitDiscovery('product_impression',{productId:tile.dataset.productId,rank:Number(tile.dataset.rank),cell:tile.dataset.cell});
      },1000));
    }
    unwatch(tile) {
      const img=tile.querySelector('img'); if(!img)return;
      this.impressions.unobserve(img);clearTimeout(this.exposureTimers.get(img));
      this.exposureTimers.delete(img);this.exposureEntries.delete(img);
    }
    buildFilters() {
      for (const kind of ['style','color']) {
        const row = this.querySelector(`[data-filter="${kind}"]`);
        let values = [...new Set(this.products.filter(p => this.isDiscoverable(p)).flatMap(p => p.facets[kind]))].sort();
        if(kind==='color')values=['light','dark','brown','blue','green','warm','rose','multicolor'].filter(value=>this.products.some(p=>this.isDiscoverable(p)&&this.colorMatches(p.facets.color,value)));
        const colorLabels={light:'Light neutrals',dark:'Dark neutrals',warm:'Warm tones',rose:'Pink & purple'};
        row.replaceChildren(this.button(kind, '', kind === 'style' ? 'All styles' : 'All colors'));
        for (const value of values) row.append(this.button(kind,value,colorLabels[value]||value.split('-').map(word => word[0].toUpperCase()+word.slice(1)).join(' ')));
        if(kind==='color'&&this.selection.color&&!values.includes(this.selection.color)){const representative=values.find(value=>this.colorMatches([this.selection.color],value));if(representative)row.querySelector(`[data-value="${representative}"]`)?.setAttribute('aria-pressed','true');}
      }
      const row = this.querySelector('[data-filter="shape"]');
      row.replaceChildren(this.button('shape','','All'),...['Area','Runner','Round'].map(value => this.button('shape',value,value)));
    }
    button(kind,value,label) {
      const b = document.createElement('button'); b.type = 'button'; b.textContent = label;
      b.dataset.label=label;
      b.dataset.kind = kind; b.dataset.value = value; b.setAttribute('aria-pressed', String(this.selection[kind] === value));
      if (kind === 'color' || kind === 'shape') {
        b.textContent = ''; b.setAttribute('aria-label',label); b.title = label;
        if (kind === 'color') {
          b.className = 'vic-swatch-button';
          const sample = document.createElement('span'); sample.className = 'vic-swatch'; sample.setAttribute('aria-hidden','true');
          const palette = {light:'#e9dfc8',dark:'#343433',warm:'#aa5148',rose:'#cb9799',beige:'#c7ad86',cream:'#e9dfc8',white:'#faf9f1',black:'#343433',brown:'#806044',blue:'#728caa',green:'#7d8a64',red:'#aa5148',pink:'#cb9799',purple:'#8b6e98',yellow:'#d4b259',orange:'#c9834e',grey:'#94948d',gray:'#94948d'};
          sample.style.setProperty('--sample-color',palette[value] || '#ddd2bc');
          if (!value) sample.classList.add('vic-swatch--all');
          if (value === 'multicolor') sample.classList.add('vic-swatch--multi');
          b.append(sample);
        } else {
          b.className = 'vic-shape-button';
          const svg = document.createElementNS('http://www.w3.org/2000/svg','svg');
          svg.setAttribute('viewBox','0 0 56 64'); svg.setAttribute('aria-hidden','true'); svg.setAttribute('focusable','false');
          const frames = {
            Area:'<path d="M10 12H46V52H10Z M13 15H43V49H13Z M12 7V12M17 7V12M22 7V12M28 7V12M34 7V12M39 7V12M44 7V12 M12 52V57M17 52V57M22 52V57M28 52V57M34 52V57M39 52V57M44 52V57"/>',
            Runner:'<path d="M17 8H39V56H17Z M20 11H36V53H20Z M19 3V8M25 3V8M31 3V8M37 3V8 M19 56V61M25 56V61M31 56V61M37 56V61"/>',
            Round:'<circle cx="28" cy="32" r="21"/><circle cx="28" cy="32" r="18"/><path d="M28 7V11M28 53V57M3 32H7M49 32H53M10.3 14.3L13.2 17.2M42.8 46.8L45.7 49.7M10.3 49.7L13.2 46.8M42.8 17.2L45.7 14.3M18.4 8.9L20 12.6M36 51.4L37.6 55.1M4.9 22.4L8.6 24M47.4 40L51.1 41.6M4.9 41.6L8.6 40M47.4 24L51.1 22.4M18.4 55.1L20 51.4M36 12.6L37.6 8.9"/>'
          };
          const botanical = '<path d="M28 43V22 M28 30C20 30 19 26 20 23C25 23 28 26 28 30Z M28 35C36 35 37 31 36 28C31 28 28 31 28 35Z M28 24C24 20 25 17 28 15C31 17 32 20 28 24Z M28 40C22 40 20 37 21 34C26 34 28 37 28 40Z"/>';
          svg.innerHTML = '<g fill="none" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round">'+frames[value]+'<g transform="translate(0 4) scale(1 .88)">'+botanical+'</g></g>';
          if(!value){svg.innerHTML='<g fill="none" stroke="currentColor" stroke-width="1"><rect x="7" y="17" width="22" height="29"/><rect x="34" y="17" width="9" height="29"/><circle cx="28" cy="42" r="12" fill="white" fill-opacity=".7"/></g>';const caption=document.createElement('span');caption.className='vic-shape-caption';caption.textContent='All';b.append(svg,caption);}else b.append(svg);
        }
      }
      return b;
    }
    listen() {
      const on = (el,event,handler,options={}) => el.addEventListener(event,handler,{...options,signal:this.signal});
      on(this,'click',e => {
        const b = e.target.closest('button[data-kind]'); if (!b) return;
        this.stopMomentum();
        const {kind,value} = b.dataset;
        const next=b.getAttribute('aria-pressed')==='true'?'':value;
        if(this.selection[kind]===next)return;
        this.selection[kind] = next;
        this.querySelectorAll(`button[data-kind="${kind}"]`).forEach(btn => btn.setAttribute('aria-pressed',String(btn.dataset.value === this.selection[kind])));
        this.emitDiscovery('filter', { kind, value: this.selection[kind] });
        this.transitionProducts(() => this.readProducts());
      });
      on(this.querySelector('[data-reset]'),'click',() => {
        this.stopMomentum();
        if(!Object.values(this.selection).some(Boolean))return;
        const focused=document.activeElement===this.querySelector('[data-reset]');
        this.selection = {shape:'',style:'',color:'',origin:'',construction:''}; this.x = this.y = 0; this.emitDiscovery('filter_reset'); this.transitionProducts(() => {this.readProducts(); this.buildFilters();});
        if(focused)this.querySelector('button[data-kind="shape"][data-value=""]')?.focus({preventScroll:true});
      });
      on(this.viewport,'wheel',e => {
        if (e.ctrlKey || this.dataset.compact==='true') return;
        this.stopMomentum();
        this.finishTransition('gesture');
        e.preventDefault();
        const factor = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? this.viewport.clientHeight : 1;
        this.x -= (e.shiftKey ? e.deltaY : e.deltaX) * factor;
        this.y -= (e.shiftKey ? 0 : e.deltaY) * factor; this.schedule();
      },{passive:false});
      on(this.viewport,'pointerdown',e => {
        if (e.button !== 0 || !e.isPrimary || this.dataset.compact==='true') return;
        this.stopMomentum();this.finishTransition('gesture');this.suppressClick=false;
        this.drag = {id:e.pointerId,x:e.clientX,y:e.clientY,startX:e.clientX,startY:e.clientY,moved:false,time:e.timeStamp,vx:0,vy:0};
      });
      on(this.viewport,'pointermove',e => {
        const d = this.drag; if (!d || e.pointerId !== d.id) return;
        if (Math.hypot(e.clientX-d.startX,e.clientY-d.startY)>6) {
          d.moved = true; this.viewport.setPointerCapture(e.pointerId); this.viewport.classList.add('is-dragging');
        }
        if (d.moved) { const dt=Math.max(1,e.timeStamp-d.time),dx=e.clientX-d.x,dy=e.clientY-d.y;
          d.vx=d.vx*.35+Math.max(-2,Math.min(2,dx/dt))*.65;d.vy=d.vy*.35+Math.max(-2,Math.min(2,dy/dt))*.65;
          this.x += dx; this.y += dy; this.schedule(); }
        d.time=e.timeStamp;
        d.x=e.clientX; d.y=e.clientY;
      });
      const end = e => { if (!this.drag || this.drag.id!==e.pointerId) return; const d=this.drag;this.suppressClick=d.moved;this.drag=null;this.viewport.classList.remove('is-dragging');
        if(e.type==='pointerup'&&d.moved&&e.timeStamp-d.time<80)this.coast(d.vx,d.vy);
      };
      on(this.viewport,'pointerup',end); on(this.viewport,'pointercancel',end);
      on(this.viewport,'click',e => { if(this.suppressClick){e.preventDefault();this.suppressClick=false;} },{capture:true});
      on(this.viewport,'click',e => {
        if (e.defaultPrevented) return;
        const tile=e.target.closest('a[data-product-id]'); if (!tile) return;
        const product=this.products.find(p=>p.dataset.productId===tile.dataset.productId);
        if(product) this.learn(product, 1);
        this.emitDiscovery('product_click', { productId:tile.dataset.productId, rank:Number(tile.dataset.rank), cell:tile.dataset.cell });
      });
      on(this.viewport,'dragstart',e=>e.preventDefault());
      on(document,'visibilitychange',()=>{if(document.hidden){this.stopMomentum();this.drag=null;this.viewport.classList.remove('is-dragging')}});
      on(window,'pagehide',()=>this.stopMomentum());
      const motion=matchMedia('(prefers-reduced-motion: reduce)');on(motion,'change',()=>{if(motion.matches){this.stopMomentum();this.finishTransition('gesture')}});
      on(this.viewport,'keydown',e => {
        const step= e.shiftKey ? 420 : 160;
        const delta={ArrowLeft:[step,0],ArrowRight:[-step,0],ArrowUp:[0,step],ArrowDown:[0,-step] }[e.key];
        if (!delta || this.dataset.compact==='true') return; this.stopMomentum();this.finishTransition('gesture');e.preventDefault(); this.viewport.focus({preventScroll:true}); this.x+=delta[0];this.y+=delta[1];this.schedule();
      });
    }
    finishTransition() {
      this.departing?.remove();this.departing=null;
      this.field?.getAnimations().forEach(animation=>animation.cancel());
    }
    transitionProducts(update) {
      this.finishTransition();
      const reduced=matchMedia('(prefers-reduced-motion: reduce)').matches;
      const previous=!reduced && this.cells.size ? this.field.cloneNode(true) : null;
      if(previous){
        previous.removeAttribute('data-field');previous.classList.add('vic-departing');
        previous.setAttribute('aria-hidden','true');previous.inert=true;
        previous.querySelectorAll('a').forEach(a=>a.tabIndex=-1);
      }
      update();this.x=this.y=0;this.clear();this.render();
      if(reduced)return;
      if(previous){
        this.departing=previous;this.viewport.append(previous);
        const fade=previous.animate([{opacity:1},{opacity:0}],{duration:180,easing:'ease-out',fill:'forwards'});
        fade.finished.then(()=>{previous.remove();if(this.departing===previous)this.departing=null;}).catch(()=>previous.remove());
      }
      this.field.animate([{opacity:0},{opacity:1}],{duration:220,easing:'cubic-bezier(.2,.7,.2,1)'});
      if(!this.notice.hidden)this.notice.animate([{opacity:0},{opacity:1}],{duration:160,easing:'ease-out'});
    }
    clear() { for(const tile of this.cells.values()) this.unwatch(tile); this.cells.clear(); this.field.replaceChildren(); }
    schedule() { if(this.dataset.vorugBootstrap==='pending') return; if(!this.baseRenderReady && this.render===VorugInfiniteCollection.prototype.render) return; if(this.frame) return; this.frame=requestAnimationFrame(()=>{this.frame=0;this.render();}); }
    render() {
      if(this.dataset.vorugBootstrap==='pending') return;
      if(!this.baseRenderReady) return;
      const width=this.viewport.clientWidth,height=this.viewport.clientHeight;
      const w= width<600 ? width/2 : width<1000 ? width/3 : width/4;
      const h=w*1.26;
      const gcd=(a,b)=>b ? gcd(b,a%b) : a;
      let stride=Math.ceil(width/w);
      while(this.filtered.length && gcd(stride,this.filtered.length)!==1) stride++;
      const firstCol=Math.floor(-this.x/w)-1,firstRow=Math.floor(-this.y/h)-1;
      const cols=Math.ceil(width/w)+2,rows=Math.ceil(height/h)+2,keep=new Set(),n=this.filtered.length;
      if (!n) {this.clear();return;}
      for(let row=firstRow;row<firstRow+rows;row++) for(let col=firstCol;col<firstCol+cols;col++) {
        const key=`${col}:${row}`;keep.add(key);
        let tile=this.cells.get(key);
        const index=((col+row*stride)%n+n)%n;
        if (!tile || tile.href !== this.filtered[index].href) {
          if(tile) { this.unwatch(tile); tile.remove(); }
          tile=this.filtered[index].cloneNode(true);tile.className='vic-image';tile.removeAttribute('data-tags');tile.removeAttribute('data-type');
          this.prepareImage(tile.querySelector('img'));tile.querySelector('img').loading='eager'; tile.querySelector('img').draggable=false;
          tile.dataset.rank=String(index+1);tile.dataset.cell=key;
          this.field.append(tile);this.cells.set(key,tile);this.impressions.observe(tile.querySelector('img'));
          const img=tile.querySelector('img');
          if(!img.complete || !img.naturalWidth) img.classList.add('vic-image-pending');
          img.addEventListener('load',()=>{img.classList.remove('vic-image-pending');this.armExposure(img);},{once:true,signal:this.signal});
          img.addEventListener('error',()=>{img.classList.remove('vic-image-pending');},{once:true,signal:this.signal});
        }
        const left=col*w+this.x,top=row*h+this.y;
        tile.style.cssText=`width:${w}px;height:${h}px;transform:translate3d(${left}px,${top}px,0)`;
        const visible=left+w>0 && left<width && top+h>0 && top<height-160;
        tile.tabIndex=visible ? 0 : -1; tile.setAttribute('aria-hidden',String(!visible));
      }
      for(const [key,tile] of this.cells) if(!keep.has(key) && tile!==document.activeElement){this.unwatch(tile);tile.remove();this.cells.delete(key);}
    }
    async loadRemaining() {
      let next=this.source.dataset.next;
      const setState=value=>{this.dataset.catalogueState=value;this.dispatchEvent(new CustomEvent('vorug:catalogue-state'));};
      setState(next?'loading':'ready');
      try {
        while(next && !this.signal.aborted) {
          const url=new URL(next,location.href);url.searchParams.set('section_id',this.dataset.section);
          const response=await fetch(url,{signal:this.signal});if(!response.ok)throw new Error('Catalogue unavailable');
          const doc=new DOMParser().parseFromString(await response.text(),'text/html');
          const source=doc.querySelector('[data-source]');if(!source)throw new Error('Catalogue unavailable');
          const known=new Set(this.products.map(a=>a.href));
          this.products.push(...[...source.querySelectorAll('a')].filter(a=>!known.has(a.href)));
          next=source.dataset.next; if(this.dataset.journey==='on'){this.dispatchEvent(new CustomEvent('vorug:catalogue-ready'));}else this.transitionProducts(() => {this.readProducts();this.buildFilters();});
        }
        setState('ready');
      } catch(error) {
        if(error.name==='AbortError')return;
        setState('failed');
        this.status.textContent='Some rugs could not load. Reload to try again.';
        const retry=this.querySelector('[data-load-error]');retry.hidden=false;
      }
    }
  }
  if(!customElements.get('vorug-infinite-collection')) customElements.define('vorug-infinite-collection',VorugInfiniteCollection);
})();
